import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingPopupComponent } from 'src/app/components/ui/loading-popup/loading-popup.component';
import { DeviceService } from 'src/app/services/devices/device.service';
import { ModalService } from 'src/app/services/modal/modal.service';
import { findLumZoneByCity, lumCitiesByRegion, lumTimeZoneRegionIds } from 'src/assets/data/LumTimeZones';
import { SuntimesService } from 'src/app/services/sunTimes/sunTimesLegacy.service';
import * as L from 'leaflet';
import tzlookup from 'tz-lookup';

interface AddressSuggestion {
  name: string;
  lat: number;
  lng: number;
}

/** Chincheta sin la sombra rectangular del icono por defecto de Leaflet. */
const markerIcon = L.icon({
  iconUrl: 'assets/marker-icon.png',
  iconRetinaUrl: 'assets/marker-icon-2x.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});

@Component({
  selector: 'app-lum-astcal',
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './lum-astcal.component.html',
  styleUrl: './lum-astcal.component.scss',
})
export class LumAstcalComponent implements OnInit, OnDestroy {

  @Input() allSettings = false;

  device = '';
  loading = false;
  saving = false;
  astcalData: any = null;

  latitude: number | null = null;
  longitude: number | null = null;
  originalLat: number = 0;
  originalLng: number = 0;

  originalData:any = null;
  changesMade = false;
  successMessage = false;
  error = '';
  timeZone: string = '';
  timezone_city: string = 'Europe/Madrid';
  region = 'Europe';
  regionIds = lumTimeZoneRegionIds();
  cities: string[] = lumCitiesByRegion('Europe');


  timezoneAutoVisual = false;
  sunriseTime = '--:--';
  sunsetTime = '--:--';
  address : string = '';
  addressSuggestions: AddressSuggestion[] = [];
  private addressSearchTimer?: ReturnType<typeof setTimeout>;
  map: L.Map | null = null;
  marker: L.Marker | null = null;
  timezoneAutoError = false;

  private modalLoad: NgbModalRef | null = null;


  constructor(
    private _aRoute: ActivatedRoute,
    public _device: DeviceService,
    private _modal: ModalService,
    private translate: TranslateService,
    private _sunTimes: SuntimesService,
  ) {}


  ngOnInit(): void {
    this._aRoute.parent?.parent?.params.subscribe((params: Params) => {
      this.device = params['serial'];
      this.loadAstcalData();
    });
  }

  ngOnDestroy(): void {
    clearTimeout(this.addressSearchTimer);
  }



  private loadAstcalData(preserveSpinner = false): void {
    if (!this.device) {
      return;
    }

    if (!preserveSpinner) {
      this.loading = true;
      this.astcalData = null;
    }

    this._device.getModuleInfo(this.device, 'astcal').subscribe({
      next: (data) => {
        this.astcalData = data;
        this.originalData = structuredClone(this.astcalData);
        this.applyData();
      },
    });
  }

  private applyData(): void {
    this.latitude = Number(this.astcalData.cfg.latitude) ;
    this.longitude = Number(this.astcalData.cfg.longitude );
    this.originalLat = this.latitude;
    this.originalLng = this.longitude;
    const storedZone = String(this.astcalData?.cfg?.timezone_city ?? '').trim();
    const found = findLumZoneByCity(storedZone || 'Europe/Madrid');
    this.region = found?.region ?? (storedZone ? '' : 'Europe');
    this.timezone_city = found?.name ?? (storedZone || 'Europe/Madrid');
    this.timeZone = found?.posix ?? this.astcalData?.cfg?.timezone ?? '';
    this.cities = lumCitiesByRegion(this.region);
    this.timezoneAutoVisual = this.timezoneMatchesLocation();
    this.timezoneAutoError = false;
    this.refreshSunTimes();
    this.loading = false;
    // El #map está en *ngIf; hay que esperar a que Angular pinte el DOM.
    setTimeout(() => this.initMap(), 0);
  }

  onRegionChange(): void {
    this.cities = lumCitiesByRegion(this.region);
    if (!this.cities.includes(this.timezone_city)) {
      this.timezone_city = '';
      this.timeZone = '';
    }
    this.onFormChange();
  }

  // Si la zona guardada corresponde a la de las coordenadas actuales, se muestra el interruptor de zona horaria automática.
  timezoneMatchesLocation(): boolean {
    return this.locationZone()?.name === this.timezone_city;
  }

  onTimezoneCityChange(): void {
    const found = findLumZoneByCity(this.timezone_city);
    this.timeZone = found?.posix ?? '';
    this.onFormChange();
  }



  onFormChange(): void {
    this.astcalData.cfg.latitude = this.latitude;
    this.astcalData.cfg.longitude = this.longitude;
    this.astcalData.cfg.timezone = this.timeZone;
    this.astcalData.cfg.timezone_city = this.timezone_city;
    this.changesMade = JSON.stringify(this.originalData) !== JSON.stringify(this.astcalData);
    this.refreshSunTimes();
  }

  //  Obtiene la zona horaria de las coordenadas actuales.
  locationZone() {
    const lat = Number(this.latitude);
    const lng = Number(this.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }
    try {
      return findLumZoneByCity(tzlookup(lat, lng));
    } catch {
      return null;
    }
  }

// Cambia el estado del interruptor de zona horaria automática.
  onChangeTimezoneAuto(enabled: boolean): void {
    this.timezoneAutoVisual = enabled;
    if (!enabled) {
      this.timezoneAutoError = false;
      return;
    }
    this.timezoneAutoError = !this.applyTimezoneFromLocation();
    if (this.timezoneAutoError) {
      this.timezoneAutoVisual = false;
      return;
    }
    this.onFormChange();
  }

  private applyTimezoneFromLocation(): boolean {
    const found = this.locationZone();
    if (!found) {
      return false;
    }
    this.region = found.region;
    this.cities = lumCitiesByRegion(found.region);
    this.timezone_city = found.name;
    this.timeZone = found.posix;
    return true;
  }

  restore(): void {
    this.astcalData = structuredClone(this.originalData);
    this.changesMade = false;
    this.applyData();
  }

  save(): void {
    this.onFormChange();
    this.saving = true;
    this.error = '';
    this.modalLoad = this._modal.open(LoadingPopupComponent);
    this.saveData();
  }

  private saveData(): void {
    const body = {
      latitude: this.astcalData.cfg.latitude,
      longitude: this.astcalData.cfg.longitude,
      timezone: this.astcalData.cfg.timezone,
      timezone_city: this.astcalData.cfg.timezone_city,
    };
    this._device.setModuleCfg(this.device, body, 'astcal').subscribe({
      next: () => {
        this.modalLoad?.close();
        this.saving = false;
        this.successMessage = true;
        setTimeout(() => {
          this.successMessage = false;
        }, 5000);
        this.loadAstcalData(true);
      },
      error: (err) => {
        this.modalLoad?.close();
        this.saving = false;
        this.error = err?.error?.message || this.translate.instant('errorUpdate');
        setTimeout(() => {
          this.error = '';
        }, 10000);
      },
    });
    this.saveGeolocation();
  }


  saveGeolocation(): void {
    let body = {
      latitude: this.latitude,
      longitude: this.longitude,
    }
    this._device.setModuleCfgRemote(this.device, body, 'geolocation').subscribe((data) => {
      console.log('Geolocalización guardada correctamente');
    }, err => {
      console.error('Error al guardar la geolocalización', err);
    })

  }


  initMap(): void {
    const container = document.getElementById('lum-astcal-map');
    if (!container) {
      return;
    }

    if (this.map) {
      this.map.remove();
      this.map = null;
    }

    this.map = L.map(container).setView([this.latitude!, this.longitude!], 17);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(this.map);

    setTimeout(() => this.map?.invalidateSize(), 0);

       // Añadir marcador inicial
       this.marker = L.marker([this.latitude!, this.longitude!], { icon: markerIcon }).addTo(this.map);
       this.changeGeolocation(this.latitude!, this.longitude!);

       // Evento: hacer clic para mover marcador
        this.map.on('click', (e: L.LeafletMouseEvent) => {
          this.latitude = this.roundGeoCoord(e.latlng.lat);
          this.longitude = this.roundGeoCoord(e.latlng.lng);

          this.marker?.setLatLng([this.latitude!, this.longitude!]);
          this.changeGeolocation(this.latitude!, this.longitude!);
        });
  }

  onAddressInput(): void {
    clearTimeout(this.addressSearchTimer);
    const query = this.address.trim();
    if (query.length < 2) {
      this.addressSuggestions = [];
      return;
    }
    this.addressSearchTimer = setTimeout(() => this.searchAddress(query), 300);
  }

  selectAddressSuggestion(suggestion: AddressSuggestion): void {
    this.address = suggestion.name;
    this.addressSuggestions = [];
    this.latitude = this.roundGeoCoord(suggestion.lat);
    this.longitude = this.roundGeoCoord(suggestion.lng);
    this.map?.setView([this.latitude, this.longitude], 17);
    this.marker?.setLatLng([this.latitude, this.longitude]);
    this.changeGeolocation(this.latitude, this.longitude);
  }

  private searchAddress(query: string): void {
    const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&limit=5&q=${encodeURIComponent(query)}`;
    fetch(url)
      .then((res) => res.json())
      .then((results: { display_name?: string; lat?: string; lon?: string }[]) => {
        if (this.address.trim() !== query) {
          return;
        }
        this.addressSuggestions = (results ?? [])
          .map((item) => ({
            name: item.display_name ?? '',
            lat: Number(item.lat),
            lng: Number(item.lon),
          }))
          .filter((item) => item.name && Number.isFinite(item.lat) && Number.isFinite(item.lng));
      })
      .catch((err) => {
        console.error('Error al buscar la dirección', err);
        this.addressSuggestions = [];
      });
  }

  /** Mismo cálculo que ANC+: calculateSuntimesLegacy, solo para hoy. */
  private refreshSunTimes(): void {
    const lat = Number(this.latitude);
    const lng = Number(this.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      this.sunriseTime = '--:--';
      this.sunsetTime = '--:--';
      return;
    }

    const today = new Date();
    const day = this._sunTimes.calculateSuntimesLegacyForWeek(
      today,
      this.gmtMinutes(this.zoneForSunTimes(lat, lng), today),
      lat,
      lng,
    )[0];
    this.sunriseTime = day?.sunriseStr ?? '--:--';
    this.sunsetTime = day?.sunsetStr ?? '--:--';
  }

  private zoneForSunTimes(lat: number, lng: number): string {
    if (this.timezone_city?.includes('/')) {
      return this.timezone_city;
    }
    try {
      return tzlookup(lat, lng);
    } catch {
      return '';
    }
  }

  private gmtMinutes(timeZone: string, date: Date): number {
    if (!timeZone) {
      return -date.getTimezoneOffset();
    }
    try {
      const label = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'shortOffset',
      }).formatToParts(date).find((part) => part.type === 'timeZoneName')?.value ?? '';
      const match = label.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
      if (!match) {
        return 0;
      }
      const sign = match[1] === '-' ? -1 : 1;
      return sign * (Number(match[2]) * 60 + Number(match[3] ?? 0));
    } catch {
      return -date.getTimezoneOffset();
    }
  }

  /** Coordenadas con precisión fija (6 decimales ≈ 0,1 m). */
  private roundGeoCoord(value: number, decimals = 6): number {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
  }

  changeGeolocation(lat: number, lng: number): void {
    if (this.timezoneAutoVisual) {
      this.timezoneAutoError = !this.applyTimezoneFromLocation();
    }
    this.onFormChange();

    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`)
      .then(res => res.json())
      .then(data => {
        this.address = data.display_name;
      })
      .catch(err => {
        console.error('Error en geolocalización inversa', err);
      });
  }


}
